import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  id: "medical-menu-categories",
  class: "main-content-wrapper"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h1", { class: "main-content-page-title" }, "カテゴリ", -1)
const _hoisted_3 = { class: "live-message-templates-body" }
const _hoisted_4 = { class: "w-full text-right pr-4" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "grid" }
const _hoisted_8 = { class: "col-12" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("label", { class: "font-normal mb-2 block" }, [
  /*#__PURE__*/_createTextVNode("名前 "),
  /*#__PURE__*/_createElementVNode("span", { class: "pl-1 text-red" }, "必須")
], -1)
const _hoisted_10 = { class: "col-12" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("label", { class: "font-normal mb-2 block" }, "順番", -1)
const _hoisted_12 = { class: "col-12 flex justify-content-center mt-2" }
const _hoisted_13 = {
  class: "flex flex-column",
  style: {"flex":"1"}
}
const _hoisted_14 = ["innerHTML"]

import { onBeforeMount, ref, inject, computed } from "vue";
import { get, set } from "@vueuse/core";

// PrimeVue関連のインポート
import { PageState } from "primevue/paginator";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import Toast from "primevue/toast";

// ユーティリティのインポート
import moment from "moment";

// 定数のインポート
import { DEFAULT_PAGINATOR_ROWS } from "@/clinic/typings/constants/paginator";

// コンポーネントのインポート
import Header from "@/clinic/components/atoms/Grid/header.vue";
import Grid from "@/clinic/components/atoms/Grid/index.vue";
import Paginator from "@/clinic/components/atoms/Paginator/index.vue";

// サービスのインポート
import { MedicalMenuCategoryServices } from "@/clinic/services";

// 型定義のインポート
import type { ApiBasePaginatorReq, ApiMedicalMenuCategoryIndexReq, ApiMedicalMenuCategoryReq } from "@/clinic/typings";

// ユーティリティ関数のインポート
import { notityErrorMsg, notifySuccessMsg } from "@/clinic/utils/notify-utils";

/** 確認ダイアログのインスタンス */

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

/**
 * メディカルメニューカテゴリ管理ページ
 * メニューカテゴリの一覧表示、作成、編集、削除機能を提供する
 */

// Vue関連のインポート
const confirm = useConfirm();

/** トースト通知のインスタンス */
const toast = useToast();

/** クリニックID */
const clinicId = ref(0);

/** データ読み込み状態 */
const loading = ref(true);

/** カテゴリ一覧 */
const categories = ref<Record<string, unknown>[]>([]);

/** 選択中のカテゴリ（編集用） */
const selectedCategory = ref<Record<string, unknown>>({});

/** フォームダイアログの表示状態 */
const formVisible = ref(false);

/** フォームダイアログのヘッダー */
const formHeader = ref("");

/**
 * グリッドのカラム定義
 * 名前、順番、作成日、アクションの4カラムを定義
 */
const columns = ref([
  { field: "name", header: "名前", class: "col-6" },
  { field: "position_number", header: "順番", class: "col-2" },
  { field: "created_at", header: "作成日", class: "col-2" },
  { field: "actions", header: "", class: "col-2" },
]);

/** 検索キーワード */
const searchTerm = ref("");

/** ページあたりの表示件数 */
const rows = ref(DEFAULT_PAGINATOR_ROWS);

/** 総件数 */
const totalCount = ref(0);

/** 現在のページ番号 */
const page = ref(0);

/** ページネーションの開始位置 */
const first = ref(0);

/** 上部ページネーターのテンプレート参照 */
const pgTop = ref<InstanceType<typeof Paginator> | null>(null);

/** 下部ページネーターのテンプレート参照 */
const pgBot = ref<InstanceType<typeof Paginator> | null>(null);

/**
 * ページネーション用のパラメータ
 * @returns {ApiBasePaginatorReq} ページネーション用のリクエストパラメータ
 */
const paginatorParams = computed(
  (): ApiBasePaginatorReq =>
    ({
      term: get(searchTerm),
      page: get(page) + 1, // バックエンドは1ベースのページ番号を期待
      rows: get(rows),
    } as ApiBasePaginatorReq)
);

/**
 * カテゴリ一覧を取得
 * APIからカテゴリデータを取得して状態を更新する
 */
const fetchCategories = async () => {
  set(loading, true);
  const { data } = await MedicalMenuCategoryServices.fetchCategories(
    { ...{ clinic_id: get(await get(clinicId)) }, ...get(paginatorParams) } as ApiMedicalMenuCategoryIndexReq,
    () => undefined,
    () => undefined,
    () => set(loading, false)
  );
  set(categories, data.value.categories);
  set(totalCount, data.value.meta.total_count);
};

/**
 * ページネーション処理
 * @param {string} fromId - ページネーターのID
 * @param {PageState} pageState - ページ状態情報
 */
const paging = async (fromId: string, { page: newPage, first: newFirst }: PageState) => {
  // 現在のページと同じ場合は何もしない
  if (newPage === get(page)) return;

  // ページ番号を更新
  set(page, newPage);

  // firstの値を更新（PrimeVueから渡された値を使用）
  set(first, newFirst);

  await fetchCategories();
};

/**
 * 検索処理
 * @param {Event} event - 検索イベント
 */
const onSearch = async (event: Event) => {
  const target = event.currentTarget as HTMLInputElement;
  set(searchTerm, target?.value || "");
  // 検索時はページをリセット
  set(page, 0);
  set(first, 0);
  await fetchCategories();
};

/**
 * カテゴリ作成ダイアログを表示
 */
const addCategory = () => {
  set(formVisible, true);
  set(formHeader, "カテゴリの作成");
};

/**
 * カテゴリ編集ダイアログを表示
 * @param {Event} event - クリックイベント
 * @param {number} categoryId - カテゴリID
 */
const editCategory = (event: Event, categoryId: number) => {
  event.stopPropagation();
  const category = get(categories).find((category: Record<string, unknown>) => category.id == categoryId);
  set(selectedCategory, category ? { ...category } : {});
  set(formVisible, true);
  set(formHeader, "カテゴリ名の変更");
};

/**
 * カテゴリの保存処理
 * 作成または更新のAPIを呼び出し、成功時は一覧を再取得
 */
const handleSaveCategory = async () => {
  await MedicalMenuCategoryServices.saveCategory(
    { ...{ clinic_id: get(await get(clinicId)) }, ...get(selectedCategory) },
    async () => {
      notifySuccessMsg(toast, "正常に保存");
      await fetchCategories();
      closePopupForm();
    },
    (errors: string[]) => notityErrorMsg(toast, errors.join("<br />"))
  );
};

/**
 * カテゴリの削除処理
 * 確認ダイアログを表示し、承認時は削除APIを呼び出し
 * @param {Event} event - クリックイベント
 * @param {number} categoryId - カテゴリID
 */
const deleteCategory = async (event: Event, categoryId: number) => {
  event.stopPropagation();

  confirm.require({
    message: "このカテゴリを削除しますか?",
    header: "カテゴリの削除",
    rejectLabel: "キャンセル",
    acceptLabel: "削除",
    acceptClass: "p-button-danger",
    accept: async () => {
      await MedicalMenuCategoryServices.deleteCategory(
        { ...{ clinic_id: get(await get(clinicId)) }, ...{ id: categoryId } } as ApiMedicalMenuCategoryReq,
        async () => {
          notifySuccessMsg(toast, "正常に削除されました");
          await fetchCategories();
        },
        () => undefined
      );
    },
  });
};

/**
 * フォームダイアログを閉じる
 * 選択中のカテゴリをリセットし、ダイアログを非表示にする
 */
const closePopupForm = () => {
  set(selectedCategory, {});
  set(formVisible, false);
};

onBeforeMount(async () => {
  set(clinicId, await inject("clinicId"));
  fetchCategories();
});

return (_ctx: any,_cache: any) => {
  const _component_InputText = _resolveComponent("InputText")!
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createVNode(Header, {
      enableSearch: "",
      onSearch: onSearch,
      onNew: addCategory,
      createButtonLabel: "作成",
      searchPlaceholder: "名前を入力"
    }),
    _createElementVNode("div", _hoisted_3, [
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 0,
            id: "pg-top",
            ref_key: "pgTop",
            ref: pgTop,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            paginatorId: "pg-top"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true),
      _createVNode(Grid, {
        items: categories.value,
        columns: columns.value,
        loading: loading.value
      }, {
        created_at: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(_unref(moment)(data.created_at).format("YYYY/MM/DD")), 1)
        ]),
        position_number: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(data.position_number), 1)
        ]),
        actions: _withCtx(({ data }) => [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("span", {
              class: "pi pi-pencil text-green-600 mr-4",
              onClick: ($event: any) => (editCategory($event, data.id))
            }, null, 8, _hoisted_5),
            _createElementVNode("span", {
              class: "pi pi-trash text-red-600",
              onClick: ($event: any) => (deleteCategory($event, data.id))
            }, null, 8, _hoisted_6)
          ])
        ]),
        _: 1
      }, 8, ["items", "columns", "loading"]),
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 1,
            id: "pg-bot",
            ref_key: "pgBot",
            ref: pgBot,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            class: "mt-2",
            paginatorId: "pg-bot"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_Dialog, {
      visible: formVisible.value,
      "onUpdate:visible": _cache[4] || (_cache[4] = ($event: any) => ((formVisible).value = $event)),
      modal: "",
      header: formHeader.value,
      style: { width: '30vw' }
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _hoisted_9,
            _createVNode(_component_InputText, {
              type: "text",
              modelValue: (selectedCategory.value as Record<string, unknown>).name,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (((selectedCategory.value as Record<string, unknown>).name) = $event)),
              placeholder: "カテゴリ名を入力してください"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _hoisted_11,
            _createVNode(_component_InputText, {
              type: "number",
              modelValue: (selectedCategory.value as Record<string, unknown>).position_number,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (((selectedCategory.value as Record<string, unknown>).position_number) = $event)),
              min: "0"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_component_Button, {
              label: "キャンセル",
              class: "p-button-light mr-5",
              onClick: closePopupForm
            }),
            _createVNode(_component_Button, {
              label: "保存",
              class: "btn-orange text-white",
              disabled: !(selectedCategory.value as Record<string, unknown>).name,
              onClick: handleSaveCategory
            }, null, 8, ["disabled"])
          ])
        ])
      ]),
      _: 1
    }, 8, ["visible", "header"]),
    _createVNode(_unref(Toast), { position: "top-center" }, {
      message: _withCtx((slotProps) => [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("span", {
            innerHTML: slotProps.message.detail
          }, null, 8, _hoisted_14)
        ])
      ]),
      _: 1
    })
  ]))
}
}

})